import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { AppConfig } from '@mdvip/shared/types/config';
import { ClientError, GraphQLClient, type RequestOptions, type Variables } from 'graphql-request';
import { getAuthAdapter } from '../auth/createAuthAdapter';
import { getSessionId } from './correlation';

let graphqlClient: GraphQLClient | null = null;

// Request-origin channel — fixed for both portals per the backend's RequestChannel
// contract (CNP-170); unlike entityId, this never varies by environment.
const CHANNEL = 'Portal';

// Backend request-attribution entity ID (RequestEntityId), set from AppConfig in
// initializeGraphQLClient. Varies per environment, unlike CHANNEL.
let entityId: string | undefined;

// Optional token provider injected by MobileAuthAdapter via createAuthAdapter.
// On web, this stays null — cookies handle auth transparently.
// On native, this returns the Keychain access token for Bearer injection.
let tokenProvider: (() => Promise<string | null>) | null = null;

/**
 * Set a token provider for per-request Bearer header injection.
 * Called by initializeAuthAdapter on native platforms; never called on web.
 */
export function setGraphQLTokenProvider(provider: () => Promise<string | null>): void {
  tokenProvider = provider;
}

/**
 * Initialize the GraphQL client with the config.
 * Must be called after config.json is loaded.
 */
export function initializeGraphQLClient(config: AppConfig): void {
  const graphqlUrl = `${config.MDVIP_API_BASE_URL}/graphql`;
  // credentials: 'include' is kept for web cookie transport; it's a no-op on native
  // since auth is provided via the Authorization header instead.
  graphqlClient = new GraphQLClient(graphqlUrl, { credentials: 'include' });
  entityId = config.ENTITY_ID;
}

/**
 * Get the GraphQL client instance.
 * Throws if not initialized.
 */
export function getGraphQLClient(): GraphQLClient {
  if (!graphqlClient) {
    throw new Error('GraphQL client not initialized. Call initializeGraphQLClient first.');
  }
  return graphqlClient;
}

/**
 * Extracts the operation name from a TypedDocumentNode AST.
 * Used to tag outgoing requests so backend logs can identify the operation
 * without re-parsing the query string.
 */
function getOperationName<TData, TVariables extends Variables>(
  document: TypedDocumentNode<TData, TVariables>,
): string | undefined {
  for (const def of document.definitions ?? []) {
    if (def.kind === 'OperationDefinition' && def.name?.value) {
      return def.name.value;
    }
  }
  return undefined;
}

/**
 * Typed GraphQL request helper with abort support.
 *
 * W3C trace headers (`traceparent`, `tracestate`, `Request-Context`) are
 * injected by the Application Insights SDK — scoped to the API host via
 * `correlationHeaderDomains` in initializeTelemetry(). This wrapper only adds
 * application-specific headers:
 *
 * - `x-session-id`        Stable per-session UUID; rotates on logout. Joins
 *                          Application Insights telemetry to backend logs
 *                          across an entire user session.
 * - `x-operation-name`    Name of the GraphQL operation (e.g. `GetMember`).
 *                          Lets backend logs identify operations without
 *                          parsing the query.
 * - `x-client-version`    Git hash of the deployed frontend build.
 * - `x-channel`           Request-origin channel for backend audit attribution.
 * - `x-entity-id`         Request-origin entity ID for backend audit attribution
 *                          (present only when `AppConfig.ENTITY_ID` is configured).
 * - `Authorization`       Bearer token (native platforms only — web uses cookies).
 *
 * On 401 / `UNAUTHENTICATED`, refreshes the session and retries once. A second
 * failure propagates to the caller.
 *
 * @example
 * const { data } = useQuery({
 *   queryKey: ['member', memberId],
 *   queryFn: ({ signal }) => gqlRequest(GetMemberDocument, { id: memberId }, signal),
 * });
 */
export async function gqlRequest<TData, TVariables extends Variables = Variables>(
  document: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
  signal?: AbortSignal,
): Promise<TData> {
  const client = getGraphQLClient();
  const operationName = getOperationName(document);

  async function execute(): Promise<TData> {
    const requestHeaders: Record<string, string> = {
      'x-session-id': getSessionId(),
      'x-client-version': __APP_VERSION__,
      'x-channel': CHANNEL,
    };
    if (operationName) {
      requestHeaders['x-operation-name'] = operationName;
    }
    if (entityId) {
      requestHeaders['x-entity-id'] = entityId;
    }
    // On native platforms, inject a per-request Authorization header from Keychain.
    // On web, tokenProvider is null and cookies handle auth automatically.
    if (tokenProvider) {
      const token = await tokenProvider();
      if (token) {
        requestHeaders.Authorization = `Bearer ${token}`;
      }
    }
    // graphql-request's RequestOptions uses a conditional type on Variables
    // that the compiler can't resolve for our generic TVariables — cast through
    // the shared shape we know matches at runtime.
    const options = {
      document,
      variables,
      requestHeaders,
      signal,
    } as unknown as RequestOptions<TVariables, TData>;
    return client.request<TData, TVariables>(options);
  }

  try {
    return await execute();
  } catch (err) {
    if (isAuthError(err)) {
      // Token expired — refresh and retry exactly once. A second failure
      // propagates to the caller (TanStack Query will surface it as an error).
      await getAuthAdapter().refresh();
      return execute();
    }
    throw err;
  }
}

function isAuthError(err: unknown): boolean {
  if (!(err instanceof ClientError)) return false;
  if (err.response.status === 401) return true;
  return err.response.errors?.some((e) => e.extensions?.code === 'UNAUTHENTICATED') ?? false;
}
